/**
 * @description 埋点辅助：型号解析、分类前缀、商品/卡片 payload 构建
 */

import {
  CATEGORY_TRACKING_PREFIX_BY_INDEX,
  CATEGORY_TRACKING_PREFIX_BY_NAME,
} from './constants'

/**
 * @description 将事件名规范为 PV 前缀（UV 由后台根据 PV 去重计算，前端只上报 PV）
 * @param {string} eventName
 * @returns {string}
 */
export function toPvEventName(eventName) {
  return String(eventName || '').replace(/^uv_/, 'pv_')
}

/**
 * @description 从商品对象解析产品型号（对齐业务字段 modelNo / productModelNo / prodModel，并兼容埋点别名）
 * @param {Record<string, any>|string|null|undefined} source
 * @returns {string}
 */
export function resolveProductModelNo(source) {
  if (!source) return ''
  if (typeof source === 'string') return source.trim()
  const candidates = [
    source.modelNo,
    source.prodModel,
    source.productModelNo,
    source.productModel,
    source.modelNumber,
    source.model_no,
    source.product_model_no,
    source.modelName,
    source.model,
    source.prodCode,
    source.productCode,
  ]
  for (const candidate of candidates) {
    if (candidate === undefined || candidate === null) continue
    const value = String(candidate).trim()
    if (value) return value
  }
  return ''
}

/**
 * @description 从商品对象解析展示名称（对齐 gcb-xcx：兼容 skuName / prodBrief）
 * @param {Record<string, any>|null|undefined} source
 * @returns {string}
 */
export function resolveProductName(source) {
  if (!source || typeof source !== 'object') return ''
  const candidates = [
    source.prodName,
    source.productName,
    source.skuName,
    source.prodBrief,
    source.name,
    source.title,
    source.packageName,
  ]
  for (const candidate of candidates) {
    if (candidate === undefined || candidate === null) continue
    const value = String(candidate).trim()
    if (value) return value
  }
  return ''
}

/**
 * @description 从商品/活动对象解析活动 ID 与名称（供活动维度分析）
 * @param {Record<string, any>|null|undefined} source
 * @returns {{ activityId: string, activityName: string, activityType: string }}
 */
export function resolveActivityTrackFields(source) {
  if (!source || typeof source !== 'object') {
    return { activityId: '', activityName: '', activityType: '' }
  }
  const activityId = String(
    source.activityId ??
      source.promotionId ??
      source.activity_id ??
      source.promotion_id ??
      ''
  ).trim()
  const activityName = String(
    source.activityName ??
      source.promotionName ??
      source.activity_name ??
      source.promotion_name ??
      source.packageName ??
      source.campaignName ??
      ''
  ).trim()
  const activityType = String(
    source.activityType ?? source.activity_type ?? source.promotionType ?? ''
  ).trim()
  return { activityId, activityName, activityType }
}

/**
 * @description 根据一级分类名称或序号解析埋点前缀
 * @param {{ categoryName?: string, categoryId?: string|number }} [category]
 * @param {number} [index=0]
 * @returns {string}
 */
export function resolveCategoryTrackingPrefix(category, index = 0) {
  const name = String(category?.categoryName || '').trim()
  if (name && CATEGORY_TRACKING_PREFIX_BY_NAME[name]) {
    return CATEGORY_TRACKING_PREFIX_BY_NAME[name]
  }
  return CATEGORY_TRACKING_PREFIX_BY_INDEX[index] || 'HomeNetwork'
}

/**
 * @description 构建商品卡片埋点公共字段
 * @param {Record<string, any>} item
 * @returns {{ targetId: string, targetName: string, modelNo: string, properties: Record<string, string> }}
 */
export function buildProductTrackFields(item) {
  // 真实商品 ID 优先；避免活动卡复合 id（activityId-prodId）污染 prod_id
  const rawProdId = item?.prodId ?? item?.productId ?? null
  const hasRealProdId = rawProdId !== undefined && rawProdId !== null && String(rawProdId).trim() !== ''
  const targetId = hasRealProdId
    ? String(rawProdId).trim()
    : String(item?.id ?? '')
  const modelNo = resolveProductModelNo(item)
  let targetName = resolveProductName(item)
  // 无品名时用型号兜底，禁止用 ID 冒充名称（监控侧会回退成 ID/ID）
  if (!targetName && modelNo) targetName = modelNo
  if (targetName && targetId && targetName === targetId) {
    targetName = modelNo && modelNo !== targetId ? modelNo : ''
  }
  return {
    targetId: targetId || modelNo || targetName || 'unknown',
    targetName,
    modelNo,
    product: item,
    properties: {
      entity_type: 'prod',
      model_no: modelNo,
      prod_name: targetName,
      prod_id: hasRealProdId ? String(rawProdId).trim() : targetId && targetId !== 'unknown' ? targetId : '',
    },
  }
}

/**
 * @description 构建方案卡片埋点公共字段（对齐需求 scheme_id / scheme_name）
 * @param {Record<string, any>} item
 * @returns {{ targetId: string, targetName: string, scheme: Record<string, any>, properties: Record<string, string> }}
 */
export function buildSchemeTrackFields(item) {
  const targetId = String(item?.solutionId ?? item?.id ?? '')
  const targetName = String(item?.solutionName ?? item?.title ?? item?.name ?? '').trim()
  return {
    targetId: targetId || targetName || 'unknown',
    targetName,
    scheme: item,
    properties: {
      entity_type: 'scheme',
      scheme_id: targetId,
      scheme_name: targetName,
    },
  }
}

/**
 * @description 构建分类卡片埋点公共字段
 * @param {Record<string, any>} item
 * @returns {{ targetId: string, targetName: string, properties: Record<string, string> }}
 */
export function buildCategoryTrackFields(item) {
  const targetId = String(item?.categoryId ?? item?.id ?? '')
  const targetName = String(item?.categoryName ?? item?.name ?? '').trim()
  return {
    targetId: targetId || targetName || 'unknown',
    targetName,
    properties: {
      category_id: targetId,
      category_name: targetName,
      card_type: 'category',
    },
  }
}

/**
 * @description 判断卡片是否为分类类型
 * @param {Record<string, any>} item
 * @returns {boolean}
 */
export function isCategoryCard(item) {
  const categoryId = item?.categoryId || item?.category?.id
  return Boolean(categoryId) && !item?.prodId && !item?.productId
}
